import React, { useEffect } from 'react';
import { Authenticator } from '@aws-amplify/ui-react';
import { I18n } from 'aws-amplify/utils';
import { useNavigate } from 'react-router-dom';
import styled from 'styled-components';
import { useAuth } from '../context/AuthContext';
import { customTexts } from '../i18n/customTexts';


// Configure custom text
I18n.putVocabularies(customTexts);

const Login = () => {
  const navigate = useNavigate();
  const { isAuthenticated, checkAuthStatus } = useAuth();

  // Redirect if already authenticated
  useEffect(() => {
    if (isAuthenticated) {
      navigate('/');
    }
  }, [isAuthenticated, navigate]);

  // Handle post-authentication
  const handleAuthSuccess = async () => {
    await checkAuthStatus(); // Refresh auth state
    navigate('/'); // Navigate to home
  };

  return (
    <Container>
      <LoginBox>
        <LogoSection>
          <Logo>KAIWA</Logo>
          <Subtitle>Survey AI Insights Platform</Subtitle>
        </LogoSection>
        
        <SignInHeading>Sign In</SignInHeading>
        
        <Authenticator
          loginMechanisms={['email']}
          signUpAttributes={['email']}
          variation="modal"
          hideSignUp={true}
        >
          {({ signOut, user }) => {
            // If user just authenticated, handle success
            if (user) {
              handleAuthSuccess();
              return <div style={{ color: '#E0E0E0', textAlign: 'center', padding: '20px' }}>Signing in...</div>;
            }
            return null;
          }}
        </Authenticator>
        
        <SecurityNotice>
          <LockIcon>🔒</LockIcon>
          <SecurityText>
            Secure authentication with AWS Cognito
            <br />
            Multiple verification options: Passkey, Authenticator app, Email, or One-time password
          </SecurityText>
        </SecurityNotice>
      </LoginBox>
    </Container>
  );
};

export default Login;

const Container = styled.div`
  min-height: 100vh;
  background: linear-gradient(135deg, #1A1A1A 0%, #2D2D2D 100%);
  display: flex;
  align-items: center;
  justify-content: center;
  padding: ${(props) => props.theme.spacing.xl};
`;

const LoginBox = styled.div`
  background-color: ${(props) => props.theme.colors.backgroundLight};
  border-radius: ${(props) => props.theme.borderRadius.xl};
  padding: ${(props) => props.theme.spacing['2xl']};
  box-shadow: ${(props) => props.theme.shadows.xl};
  max-width: 480px;
  width: 100%;
  border: 1px solid ${(props) => props.theme.colors.border};
`;

const LogoSection = styled.div`
  text-align: center;
  margin-bottom: ${(props) => props.theme.spacing.xl};
`;

const Logo = styled.h1`
  font-family: ${(props) => props.theme.fonts.heading};
  font-size: ${(props) => props.theme.fontSizes['4xl']};
  font-weight: ${(props) => props.theme.fontWeights.bold};
  color: ${(props) => props.theme.colors.primary};
  letter-spacing: 2px;
  margin-bottom: ${(props) => props.theme.spacing.xs};
`;

const Subtitle = styled.p`
  font-size: ${(props) => props.theme.fontSizes.base};
  color: ${(props) => props.theme.colors.textSecondary};
  font-weight: ${(props) => props.theme.fontWeights.regular};
`;

const SignInHeading = styled.h2`
  font-size: ${(props) => props.theme.fontSizes['2xl']};
  font-weight: ${(props) => props.theme.fontWeights.semibold};
  color: #4DD0E1;
  text-align: center;
  margin-bottom: ${(props) => props.theme.spacing.lg};
  margin-top: ${(props) => props.theme.spacing.md};
`;

const SecurityNotice = styled.div`
  margin-top: ${(props) => props.theme.spacing.xl};
  padding: ${(props) => props.theme.spacing.md};
  background-color: rgba(0, 128, 128, 0.1);
  border-radius: ${(props) => props.theme.borderRadius.md};
  border: 1px solid rgba(0, 128, 128, 0.2);
  display: flex;
  align-items: center;
  gap: ${(props) => props.theme.spacing.md};
`;

const LockIcon = styled.span`
  font-size: ${(props) => props.theme.fontSizes['2xl']};
`;

const SecurityText = styled.p`
  font-size: ${(props) => props.theme.fontSizes.sm};
  color: ${(props) => props.theme.colors.textSecondary};
  line-height: 1.5;
  margin: 0;
`;
